// ---------------------------------------------------------------------------
// CanvasViewport — renders the active page's blocks using cogo-react-renderer-v2.
//
// Each block is rendered by BlockV2 (v2 renderer) with editor interaction overlays.
// ---------------------------------------------------------------------------

import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import {
  ElementRegistry,
  registerAllRenderers,
  RenderContextV2,
  type RenderContextV2Value,
  BlockV2,
  getSharedRegistry,
} from '@cogo/react-renderer-v2'
import { RenderContext, BreakpointProvider } from '@cogo/react-renderer'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import { getPageWorkPanelCopy } from '@/features/page-editor/page-work-panel-copy'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { DeviceFrame } from './device-frame'
import { resolveGeneratedAppViewportMetadata } from '@/features/preview/viewport/generated-app-viewport'
import { viewportIsPinnedToSource } from '@/features/design-system/viewport/viewport-policy'
import { SortableBlock } from '@/features/page-editor/interactions/dnd/sortable-block'
import { BlockOverlay } from './block-overlay'
import { SelectionOverlay } from '@/features/page-editor/interactions/selection/selection-overlay'
import { LassoOverlay } from '@/features/page-editor/interactions/selection/lasso-overlay'
import { ResizeHandles } from '@/features/page-editor/interactions/selection/resize-handles'
import { HoverLabel } from '@/features/page-editor/interactions/selection/hover-label'
import { EventFlowOverlay } from '@/features/page-editor/interactions/selection/event-flow-overlay'
import { BREAKPOINTS } from '@/features/page-editor/lib/responsive'
import { useCanvasSandbox } from '@/features/page-editor/hooks/use-canvas-sandbox'
import { findElementInTree, findParentInTree } from '@/features/page-editor/store/canvas-slice'
import { isContainer } from '@/features/page-editor/lib/element-factory'
import { resolveVisibleElement } from '@/features/page-editor/lib/dom-utils'
import { SHORTCUT } from '@/lib/shortcuts'
import {
  Copy,
  Scissors,
  Clipboard,
  CopyPlus,
  WrapText,
  Trash2,
  ArrowUpToLine,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  EyeOff,
} from 'lucide-react'
import { toast } from '@/store/toast-store'
import { HiddenBlockCard } from './hidden-block-card'
import { PlaceholderRenderer } from './placeholder-renderer'
import { DataSourceBadge } from './data-source-badge'
import { extractSymbols } from '@/features/page-editor/services/symbol-extractor'
import type { BlockDef, AbsoluteBlockPosition } from '@cogo/types-v2'
import { isAbsolutePosition } from '@cogo/types-v2'
import { AbsoluteBlockWrapper } from '@/features/page-editor/interactions/absolute/absolute-block-wrapper'
import {
  buildPageEditBindingMetadata,
  formatPageEditBindingValue,
  PAGE_EDIT_RENDERING_POLICY_CSS,
  type PageEditBindingMeta,
} from './page-edit-rendering-policy'

// Create and populate the renderer registry once
const registry = getSharedRegistry()

// ---------------------------------------------------------------------------
// splitBlocksByZone — separates blocks into visible and hidden zones
// based on _editor.hidden field. Exported for testing.
// ---------------------------------------------------------------------------

type BlockEntry = { name: string; block: BlockDef }
type BindingOverlayState = {
  meta: Pick<PageEditBindingMeta, 'label' | 'hoverLabel' | 'fullLabel' | 'path' | 'valueType'>
  rect: { left: number; top: number; width: number; height: number }
}

export function splitBlocksByZone(
  pageBlockNames: string[],
  blocks: Record<string, BlockDef | undefined>,
): { visibleBlocks: BlockEntry[]; hiddenBlocks: BlockEntry[] } {
  const visible: BlockEntry[] = []
  const hidden: BlockEntry[] = []
  for (const name of pageBlockNames) {
    const block = blocks[name]
    if (!block) continue
    if (block._editor?.hidden === true) {
      hidden.push({ name, block })
    } else {
      visible.push({ name, block })
    }
  }
  return { visibleBlocks: visible, hiddenBlocks: hidden }
}

// CSS for element hover highlight — injected once
const HOVER_STYLE_ID = 'cogo-editor-hover-style'

export function CanvasViewport() {
  const { locale } = useStudioLocale()
  const pageWorkCopy = getPageWorkPanelCopy(locale)
  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)
  const blocks = useEditorStore((s) => s.blocks)
  const zoom = useEditorStore((s) => s.zoom)
  const panX = useEditorStore((s) => s.panX)
  const panY = useEditorStore((s) => s.panY)
  const isPreview = useEditorStore((s) => s.isPreview)
  const hoveredElementId = useEditorStore((s) => s.hoveredElementId)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const selectionTier = useEditorStore((s) => s.selectionTier)
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint)
  const previewColorScheme = useEditorStore((s) => s.previewColorScheme)
  const canvasRef = useRef<HTMLDivElement>(null)
  const appContentRef = useRef<HTMLDivElement>(null)

  // Block-level hover tracking (separate from element hover)
  const [hoveredBlockName, setHoveredBlockName] = useState<string | null>(null)
  const [bindingOverlay, setBindingOverlay] = useState<BindingOverlayState | null>(null)

  // Fully-isolated app theme — independent of Studio shell dark/light toggle.
  // Uses complete CSS variable set so no variables leak from the Studio shell.
  const viewMode = useStudioStore((s) => s.viewMode)
  const showDeviceFrame = viewMode !== 'editor'

  const currentProject = useStudioStore((s) => s.currentProject)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const previewDeviceChoices = useStudioStore((s) => s.previewDeviceChoices)
  const tokens = currentProject?.tokens
  const generatedViewportMetadata = useMemo(
    () => resolveGeneratedAppViewportMetadata(currentProject, activePageId),
    [activePageId, currentProject],
  )
  const generatedViewport = generatedViewportMetadata && (
    viewportIsPinnedToSource(currentProject, true) ||
    !currentProjectId ||
    previewDeviceChoices[currentProjectId] == null
  )
    ? generatedViewportMetadata
    : undefined
  // AppSandbox — 6-module isolation for in-DOM app rendering (Phase 152/153).
  // sandbox.mount() called in hook after appContentRef is available in DOM.
  // sandbox.unmount() + sandbox.destroy() fire on component cleanup.
  // sandbox.updateTokens() fires when tokens or previewColorScheme change.
  const appId = useEditorStore((s) => s.app?.appId ?? null)
  const canvasSandboxState = useCanvasSandbox({
    appId,
    tokens: tokens ?? null,
    colorScheme: previewColorScheme,
    hostRef: appContentRef,
  }) ?? { sandbox: null, ready: false }
  const { sandbox: canvasSandbox, ready: canvasSandboxReady } = canvasSandboxState
  const appRenderRoot = canvasSandboxReady ? canvasSandbox?.modules.dom.renderRoot ?? null : null

  // Inject hover/selection highlight styles (Framer-style blue, thin lines)
  useEffect(() => {
    if (document.getElementById(HOVER_STYLE_ID)) return
    const style = document.createElement('style')
    style.id = HOVER_STYLE_ID
    style.textContent = `
      [data-element-hovered="true"] {
        outline: 1px solid hsl(210 100% 50% / 0.4) !important;
        outline-offset: 0px;
      }
      [data-element-selected="true"] {
        outline: 1.5px solid hsl(210 100% 50%) !important;
        outline-offset: 0px;
      }
      [data-element-parent-highlight="true"] {
        outline: 1px dashed hsl(190 80% 55% / 0.5) !important;
        outline-offset: 0px;
      }
      /* Block-level hover: dashed foreground outline (distinct from element blue) */
      [data-block-hovered="true"]:not([data-block-selected="true"]) {
        outline: 1px dashed hsl(var(--foreground) / 0.2) !important;
        outline-offset: -1px;
      }
      /* Block-level selection: solid primary border */
      [data-block-selected="true"] {
        outline: 2px solid hsl(var(--primary) / 0.5) !important;
        outline-offset: -1px;
      }
      ${PAGE_EDIT_RENDERING_POLICY_CSS}

      /* Page edit mode: keep app controls clickable for editor selection.
         Runtime actions are blocked in EditorBlock handlers via preventDefault. */
      [data-cogo-page-edit-mode="true"] a,
      [data-cogo-page-edit-mode="true"] button,
      [data-cogo-page-edit-mode="true"] input,
      [data-cogo-page-edit-mode="true"] select,
      [data-cogo-page-edit-mode="true"] textarea {
        cursor: default !important;
      }
    `
    document.head.appendChild(style)
    return () => { document.getElementById(HOVER_STYLE_ID)?.remove() }
  }, [])

  // Reset zoom to 1.0 when breakpoint changes — let the canvas scroll naturally
  useEffect(() => {
    useEditorStore.getState().setZoom(1)
  }, [activeBreakpoint])

  // Apply hover/selection/parent highlights via data attributes
  useEffect(() => {
    const container = canvasRef.current
    if (!container || isPreview) return

    // Clear previous highlights
    container.querySelectorAll('[data-element-hovered]').forEach((el) => {
      el.removeAttribute('data-element-hovered')
    })
    container.querySelectorAll('[data-element-selected]').forEach((el) => {
      el.removeAttribute('data-element-selected')
    })
    container.querySelectorAll('[data-element-parent-highlight]').forEach((el) => {
      el.removeAttribute('data-element-parent-highlight')
    })

    // Apply hover — resolve to visible element for display:contents wrappers
    if (hoveredElementId) {
      const hoverEl = container.querySelector(`[data-element-id="${hoveredElementId}"]`) as HTMLElement | null
      if (hoverEl && !selectedIds.includes(hoveredElementId)) {
        resolveVisibleElement(hoverEl).setAttribute('data-element-hovered', 'true')
      }
    }

    // Apply selection outlines on individual elements + parent highlight
    // resolve to visible element so outline renders on a real CSS box
    for (const id of selectedIds) {
      const el = container.querySelector(`[data-element-id="${id}"]`) as HTMLElement | null
      if (el) {
        resolveVisibleElement(el).setAttribute('data-element-selected', 'true')
        // Highlight parent container with dashed outline (Framer-style)
        const parentEl = el.parentElement?.closest('[data-element-id]') as HTMLElement | null
        if (parentEl && !selectedIds.includes(parentEl.getAttribute('data-element-id') ?? '')) {
          resolveVisibleElement(parentEl).setAttribute('data-element-parent-highlight', 'true')
        }
      }
    }
  }, [hoveredElementId, selectedIds, isPreview])

  // Find the active page
  const activePage = useMemo(() => {
    if (!app || !activePageId) return undefined
    return app.pages.find((p) => p.id === activePageId)
  }, [app, activePageId])

  // Split block definitions into visible/hidden zones (CANV-01)
  const { visibleBlocks, hiddenBlocks } = useMemo(() => {
    if (!activePage?.blocks) return { visibleBlocks: [], hiddenBlocks: [] }
    return splitBlocksByZone(activePage.blocks, blocks)
  }, [activePage, blocks])

  const visibleBlockMap = useMemo(() => {
    const entries: Record<string, BlockDef | undefined> = {}
    for (const { name, block } of visibleBlocks) entries[name] = block
    return entries
  }, [visibleBlocks])

  const pageEditBindingMetadata = useMemo(
    () => buildPageEditBindingMetadata(visibleBlockMap, { app, blocks }),
    [app, blocks, visibleBlockMap],
  )

  // Flows from editor store — needed for HiddenBlockCard badges
  const flows = useEditorStore((s) => s.flows)

  // Hidden section collapse state (default expanded per user decision)
  const [hiddenExpanded, setHiddenExpanded] = useState(true)

  // Build the RenderContext for cogo-react-renderer
  const renderCtx = useMemo<RenderContextV2Value>(
    () => ({
      registry,
      resolveSymbol: (expr: string) => {
        if (typeof expr === 'string' && expr.includes('#{')) {
          if (!isPreview) return formatPageEditBindingValue(expr, { app, blocks })
          return expr
        }
        return expr
      },
      dispatchEvent: (_elementId, _eventType, _payload) => {
        // In edit mode, events are not dispatched
        // TODO: In preview mode, wire to ActionFlowEngine
      },
      mode: isPreview ? 'preview' : 'edit',
      // v2 BlockDef (meta/ui/events/flow/state/data) structurally differs from v1 CogoBlockDef
      // (schemaVersion/blockType/root). The v2 renderer reads through `blocks` dynamically;
      // the v1 `RenderContextValue.blocks` field is only accessed by v1 renderers that aren't
      // mounted here. Bridge-cast to satisfy the v1 shape contract without a runtime change.
      blocks: blocks as unknown as RenderContextV2Value['blocks'],
      portalContainer: canvasSandboxReady ? canvasSandbox?.modules.dom.portalContainer ?? null : null,
      schemaVersion: '3.0',
    }),
    [app, blocks, isPreview, canvasSandbox, canvasSandboxReady],
  )

  useEffect(() => {
    if (isPreview) return
    const host = appContentRef.current
    if (!host) return

    const elements = Array.from(host.querySelectorAll('[data-element-id]')) as HTMLElement[]
    for (const el of elements) {
      el.removeAttribute('data-cogo-binding-kind')
      el.removeAttribute('data-cogo-binding-label')
      el.removeAttribute('data-cogo-binding-hover-label')
      el.removeAttribute('data-cogo-binding-token-label')
      el.removeAttribute('data-cogo-binding-full')
      el.removeAttribute('data-cogo-binding-path')
      el.removeAttribute('data-cogo-binding-value-type')
    }

    for (const el of elements) {
      const id = el.getAttribute('data-element-id')
      if (!id) continue
      const meta = pageEditBindingMetadata.get(id)
      if (!meta) continue
      const target = resolveVisibleElement(el)
      target.setAttribute('data-cogo-binding-kind', meta.kind)
      target.setAttribute('data-cogo-binding-label', meta.label)
      target.setAttribute('data-cogo-binding-hover-label', meta.hoverLabel)
      target.setAttribute('data-cogo-binding-token-label', meta.tokenLabel)
      target.setAttribute('data-cogo-binding-full', meta.fullLabel)
      if (meta.path) target.setAttribute('data-cogo-binding-path', meta.path)
      if (meta.valueType) target.setAttribute('data-cogo-binding-value-type', meta.valueType)
      if (!target.title.includes(meta.fullLabel)) {
        target.title = target.title ? `${target.title} · ${meta.fullLabel}` : meta.fullLabel
      }
    }
  }, [isPreview, pageEditBindingMetadata, appRenderRoot])

  useEffect(() => {
    if (isPreview) {
      setBindingOverlay(null)
      return
    }
    const host = appContentRef.current
    if (!host) return

    const target = host.querySelector(
      '[data-cogo-binding-kind="variable"][data-element-selected="true"], [data-cogo-binding-kind="variable"][data-element-hovered="true"]',
    ) as HTMLElement | null

    if (!target) {
      setBindingOverlay(null)
      return
    }

    const rect = target.getBoundingClientRect()
    setBindingOverlay({
      meta: {
        label: target.getAttribute('data-cogo-binding-label') ?? 'Variable',
        hoverLabel: target.getAttribute('data-cogo-binding-hover-label') ?? 'Variable',
        fullLabel: target.getAttribute('data-cogo-binding-full') ?? 'Variable',
        path: target.getAttribute('data-cogo-binding-path') ?? undefined,
        valueType: target.getAttribute('data-cogo-binding-value-type') as PageEditBindingMeta['valueType'],
      },
      rect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      },
    })
  }, [hoveredElementId, selectedIds, isPreview, pageEditBindingMetadata, appRenderRoot])

  const handleCopyBindingPath = useCallback(async () => {
    const path = bindingOverlay?.meta.path
    if (!path) return
    try {
      await navigator.clipboard.writeText(path)
      toast.info('Variable path copied')
    } catch {
      toast.info(path)
    }
  }, [bindingOverlay])

  // Handle wheel for zoom (Ctrl/Cmd + scroll)
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        const delta = e.deltaY > 0 ? -0.05 : 0.05
        const newZoom = Math.max(0.25, Math.min(3, zoom + delta))
        useEditorStore.getState().setZoom(newZoom)
      }
    },
    [zoom],
  )

  // Click on canvas background clears selection
  const handleBackgroundClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (target === e.currentTarget || target.dataset.canvasBg === 'true') {
      useEditorStore.getState().clearSelection()
    }
  }, [])

  // Hover detection — find closest element on mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isPreview) return
      const target = e.target as HTMLElement
      const elementEl = target.closest('[data-element-id]')
      const id = elementEl?.getAttribute('data-element-id') ?? null
      const current = useEditorStore.getState().hoveredElementId
      if (id !== current) {
        useEditorStore.getState().setHoveredElement(id)
      }
    },
    [isPreview],
  )

  const handleMouseLeave = useCallback(() => {
    useEditorStore.getState().setHoveredElement(null)
  }, [])

  return (
    <div
      ref={canvasRef}
      className="relative h-full overflow-auto bg-muted/30"
      onWheel={handleWheel}
      onClick={handleBackgroundClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-canvas-bg="true"
      data-testid="canvas-viewport"
      data-cogo-app-design-sandbox={canvasSandboxReady ? 'ready' : 'mounting'}
    >
      <div
        className="inline-flex min-h-full items-start justify-center p-2 sm:p-8"
        data-canvas-bg="true"
        style={{
          transform: zoom !== 1 ? `scale(${zoom}) translate(${panX}px, ${panY}px)` : undefined,
          transformOrigin: 'top center',
          minWidth: '100%',
        }}
      >
        {(() => {
          const canvasContent = (
            <div className="relative h-full w-full">
              {/* App content — scoped with project design tokens + color scheme.
                  Editor overlays below stay on the Studio theme.
                  BreakpointProvider enables container-width-based responsive resolution
                  so Tailwind sm:/md:/lg: classes respond to DeviceFrame width, not viewport. */}
              <BreakpointProvider containerRef={appContentRef}>
              <div
                ref={appContentRef}
                className="h-full w-full"
                data-app-content="true"
                data-cogo-page-edit-mode={!isPreview ? 'true' : undefined}
                data-preview-mode={isPreview ? 'true' : undefined}
                data-cogo-app-design-root="true"
                data-cogo-app-design-sandbox-id={appId ?? undefined}
              >
                {(() => {
                  const generatedAppContent = activePage && (visibleBlocks.length > 0 || hiddenBlocks.length > 0) ? (
                  <RenderContext.Provider value={renderCtx}>
                  <RenderContextV2.Provider value={renderCtx}>
                    {visibleBlocks.length > 0 ? (
                      activePage.layout === 'absolute' ? (
                        /* Absolute layout mode — blocks positioned at x/y/w/h coordinates */
                        <div
                          style={{
                            position: 'relative',
                            width: activePage.canvas?.w ?? 1920,
                            height: activePage.canvas?.h ?? 1080,
                            minHeight: activePage.canvas?.h ?? 1080,
                          }}
                          data-testid="absolute-canvas"
                        >
                          {visibleBlocks.map(({ name, block }) => {
                            const pos = activePage.blockPositions?.[name]
                            if (!pos || !isAbsolutePosition(pos)) return null
                            const isBlockHovered = hoveredBlockName === name
                            const isBlockSelected = selectedBlockName === name && selectionTier === 'block'
                            return (
                              <AbsoluteBlockWrapper
                                key={name}
                                blockName={name}
                                position={pos}
                                isPreview={isPreview}
                                isSelected={isBlockSelected}
                              >
                                <div
                                  className="relative h-full"
                                  data-block-name={name}
                                  data-block-hovered={isBlockHovered && !isBlockSelected ? 'true' : undefined}
                                  data-block-selected={isBlockSelected ? 'true' : undefined}
                                  onMouseEnter={() => !isPreview && setHoveredBlockName(name)}
                                  onMouseLeave={() => !isPreview && setHoveredBlockName((prev) => prev === name ? null : prev)}
                                >
                                  {!isPreview && isBlockSelected && (
                                    <BlockOverlay
                                      blockName={name}
                                      label={block.meta?.name ?? name}
                                      isHovered={isBlockHovered}
                                    />
                                  )}
                                  <EditorBlock name={name} block={block} isPreview={isPreview} />
                                </div>
                              </AbsoluteBlockWrapper>
                            )
                          })}
                        </div>
                      ) : (
                    /* Flow/stack layout mode — sortable vertical list */
                    <SortableContext
                      items={visibleBlocks.map(({ name }) => name)}
                      strategy={verticalListSortingStrategy}
                    >
                      {visibleBlocks.map(({ name, block }, index) => {
                        const isBlockHovered = hoveredBlockName === name
                        const isBlockSelected = selectedBlockName === name && selectionTier === 'block'
                        return (
                          <div
                            key={name}
                            className="relative"
                            data-block-hovered={isBlockHovered && !isBlockSelected ? 'true' : undefined}
                            data-block-selected={isBlockSelected ? 'true' : undefined}
                            onMouseEnter={() => !isPreview && setHoveredBlockName(name)}
                            onMouseLeave={() => !isPreview && setHoveredBlockName((prev) => prev === name ? null : prev)}
                          >
                            {!isPreview && isBlockSelected && (
                              <BlockOverlay
                                blockName={name}
                                label={block.meta?.name ?? name}
                                isHovered={isBlockHovered}
                              />
                            )}
                            <SortableBlock
                              blockId={name}
                              pageId={activePageId!}
                              index={index}
                              label={block.meta?.name ?? name}
                              isPreview={isPreview}
                            >
                              <EditorBlock name={name} block={block} isPreview={isPreview} />
                            </SortableBlock>
                          </div>
                        )
                      })}
                    </SortableContext>
                      )
                    ) : (
                      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                        All blocks are hidden. They will be shown via ActionFlow.
                      </div>
                    )}
                  </RenderContextV2.Provider>
                  </RenderContext.Provider>
                  ) : activePage ? (
                  <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                    Drag blocks here to start building
                  </div>
                  ) : (
                  <div className="flex h-96 items-center justify-center text-sm text-muted-foreground">
                    {app ? 'No page selected.' : pageWorkCopy.loadTemplateToEdit}
                  </div>
                  )

                  if (!activePage || (visibleBlocks.length === 0 && hiddenBlocks.length === 0)) {
                    return generatedAppContent
                  }

                  if (!appRenderRoot) {
                    return (
                      <div
                        className="flex h-32 items-center justify-center text-sm text-muted-foreground"
                        data-testid="canvas-app-sandbox-loading"
                      >
                        Preparing app sandbox...
                      </div>
                    )
                  }

                  return createPortal(generatedAppContent, appRenderRoot)
                })()}
              </div>
              </BreakpointProvider>

              {!isPreview && bindingOverlay?.meta.path && (
                <div
                  className="fixed z-[80] max-w-[360px] rounded-md border border-border bg-popover px-2.5 py-2 text-popover-foreground shadow-lg"
                  style={{
                    left: Math.max(12, Math.min(bindingOverlay.rect.left, window.innerWidth - 380)),
                    top: Math.max(12, bindingOverlay.rect.top - 54),
                  }}
                  data-testid="page-binding-floating-command"
                >
                  <div className="flex items-center gap-2">
                    <div className="min-w-0">
                      <div className="text-[11px] font-medium leading-none text-muted-foreground">
                        {bindingOverlay.meta.label}
                      </div>
                      <div className="mt-1 truncate font-mono text-xs leading-tight" title={bindingOverlay.meta.path}>
                        {bindingOverlay.meta.path}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ml-auto inline-flex h-7 shrink-0 items-center gap-1 rounded border border-border bg-background px-2 text-xs font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                      onClick={handleCopyBindingPath}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </button>
                  </div>
                </div>
              )}

              {/* Hidden Blocks Zone (CANV-01) */}
              {!isPreview && hiddenBlocks.length > 0 && (
                <div className="border-t border-dashed border-border bg-muted/30 px-3 py-2">
                  <button
                    className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
                    onClick={() => setHiddenExpanded((prev) => !prev)}
                  >
                    {hiddenExpanded ? (
                      <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    <EyeOff className="h-3.5 w-3.5" />
                    <span>Hidden — ActionFlow controlled ({hiddenBlocks.length})</span>
                  </button>

                  {hiddenExpanded && (
                    <div className="mt-2 grid gap-1.5">
                      {hiddenBlocks.map(({ name, block }) => (
                        <HiddenBlockCard
                          key={name}
                          blockName={name}
                          displayName={block.meta?.name ?? name}
                          flows={flows}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Interaction overlays — outside app theme scope, on Studio theme */}
              {!isPreview && <LassoOverlay />}
              {!isPreview && <HoverLabel />}
              {!isPreview && <SelectionOverlay />}
              {!isPreview && <EventFlowOverlay />}
              {!isPreview && <ResizeHandles />}
            </div>
          )

          return showDeviceFrame ? (
            <DeviceFrame
              sourceViewport={generatedViewport}
              safeAreaInsets={generatedViewport?.safeArea}
            >
              {canvasContent}
            </DeviceFrame>
          ) : (
            <div
              className="relative overflow-hidden bg-background"
              style={{ minHeight: 600, transform: 'scale(1)' }}
            >
              {canvasContent}
            </div>
          )
        })()}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// EditorBlock — wraps a BlockV2 with selection/hover interaction
// ---------------------------------------------------------------------------

function EditorBlock({
  name,
  block,
  isPreview,
}: {
  name: string
  block: import('@cogo/types-v2').BlockDef
  isPreview: boolean
}) {
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const isSelected = selectedBlockName === name

  // Determine if this block should show a placeholder
  const placeholderType = block._editor?.placeholder
  const symbols = useMemo(() => extractSymbols(block), [block])

  // Canvas right-click context menu state
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; elementId: string } | null>(null)
  const ctxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ctxMenu) return
    const handleClick = (e: MouseEvent) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target as Node)) setCtxMenu(null)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [ctxMenu])

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isPreview) return
      e.preventDefault()
      e.stopPropagation()
      const target = e.target as HTMLElement
      const elementEl = target.closest('[data-element-id]')
      if (elementEl) {
        const elementId = elementEl.getAttribute('data-element-id')!
        useEditorStore.getState().select(elementId, name)
        setCtxMenu({ x: e.clientX, y: e.clientY, elementId })
      }
    },
    [name, isPreview],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isPreview) return
      e.preventDefault()
      e.stopPropagation()
      const target = e.target as HTMLElement
      const elementEl = target.closest('[data-element-id]')
      if (elementEl) {
        const elementId = elementEl.getAttribute('data-element-id')!
        // Shift/Cmd+Click toggles multi-select (Framer/Figma behavior)
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          const store = useEditorStore.getState()
          // Ensure we're in the same block for multi-select
          if (store.selectedBlockName === name || store.selectedIds.length === 0) {
            if (!store.selectedBlockName) store.select(elementId, name)
            else store.toggleSelection(elementId)
          } else {
            // Different block — start fresh selection
            store.select(elementId, name)
          }
        } else {
          useEditorStore.getState().select(elementId, name)
        }
      } else {
        useEditorStore.getState().selectBlock(name)
      }
    },
    [name, isPreview],
  )

  // Double-click drills into containers (Framer/Figma behavior)
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isPreview) return
      e.stopPropagation()
      const target = e.target as HTMLElement
      const elementEl = target.closest('[data-element-id]')
      if (!elementEl) return
      const elementId = elementEl.getAttribute('data-element-id')!
      const el = findElementInTree(block.ui.root, elementId)
      // If it's a container with children, select first child
      if (el && isContainer(el) && el.children && el.children.length > 0) {
        useEditorStore.getState().select(el.children[0].id, name)
      }
    },
    [name, block, isPreview],
  )

  return (
    <div
      className="relative group"
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      data-testid={`block-${name}`}
      data-block-name={name}
    >
      {/* Block label (shown on hover — Framer-style blue) */}
      {!isPreview && (
        <div
          className="absolute -top-5 left-0 z-10 hidden group-hover:flex items-center gap-1 px-1.5 py-0.5 text-white text-[10px] font-medium rounded-t"
          style={{ backgroundColor: 'hsl(var(--editor-selection))' }}
        >
          {block.meta?.name ?? name}
        </div>
      )}

      {/* Selection ring (Framer-style thin blue) */}
      {isSelected && !isPreview && (
        <div
          className="absolute inset-0 rounded pointer-events-none z-10"
          style={{ border: '1.5px solid hsl(var(--editor-selection))' }}
        />
      )}

      {/* Actual block content or placeholder for data-bound blocks */}
      {placeholderType ? (
        <div data-testid={`placeholder-${name}`}>
          <PlaceholderRenderer
            type={placeholderType}
            placeholderCount={block._editor?.placeholderCount}
          />
          <DataSourceBadge symbols={symbols} />
        </div>
      ) : (
        <BlockV2 block={block} />
      )}

      {/* Canvas right-click context menu */}
      {ctxMenu && (
        <CanvasContextMenu
          ref={ctxRef}
          x={ctxMenu.x}
          y={ctxMenu.y}
          elementId={ctxMenu.elementId}
          blockName={name}
          block={block}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// CanvasContextMenu — right-click context menu on canvas elements.
// Renders through document.body because this is Studio chrome, not generated
// application UI; keeping it outside data-app-content prevents app design tokens
// from styling Studio controls.
// ---------------------------------------------------------------------------

const CanvasContextMenu = forwardRef<
  HTMLDivElement,
  {
    x: number
    y: number
    elementId: string
    blockName: string
    block: import('@cogo/types-v2').BlockDef
    onClose: () => void
  }
>(function CanvasContextMenu({ x, y, elementId, blockName, block, onClose }, ref) {
  const element = findElementInTree(block.ui.root, elementId)
  const parentInfo = findParentInTree(block.ui.root, elementId)
  const hasParent = parentInfo != null
  const hasClipboard = useEditorStore((s) => s.clipboard.elements.length > 0)

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[100] min-w-[180px] rounded-xl border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95"
      style={{ left: x, top: y }}
      data-testid="canvas-context-menu"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Copy */}
      <button
        onClick={() => { onClose(); useEditorStore.getState().copySelection(); toast.info('Copied') }}
        className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
      >
        <span className="flex items-center gap-2"><Copy className="h-3 w-3" /> Copy</span>
        <span className="text-[10px] text-muted-foreground">{SHORTCUT.copy}</span>
      </button>

      {/* Cut */}
      <button
        onClick={() => { onClose(); useEditorStore.getState().cutSelection(); toast.info('Cut') }}
        className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
      >
        <span className="flex items-center gap-2"><Scissors className="h-3 w-3" /> Cut</span>
        <span className="text-[10px] text-muted-foreground">{SHORTCUT.cut}</span>
      </button>

      {/* Paste */}
      <button
        onClick={() => {
          onClose()
          if (hasClipboard) { useEditorStore.getState().pasteClipboard(); toast.info('Pasted') }
        }}
        disabled={!hasClipboard}
        className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent disabled:opacity-40"
      >
        <span className="flex items-center gap-2"><Clipboard className="h-3 w-3" /> Paste</span>
        <span className="text-[10px] text-muted-foreground">{SHORTCUT.paste}</span>
      </button>

      <div className="my-1 h-px bg-border" />

      {/* Duplicate */}
      <button
        onClick={() => { onClose(); useEditorStore.getState().duplicateSelection(); toast.info('Duplicated') }}
        disabled={!hasParent}
        className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent disabled:opacity-40"
      >
        <span className="flex items-center gap-2"><CopyPlus className="h-3 w-3" /> Duplicate</span>
        <span className="text-[10px] text-muted-foreground">{SHORTCUT.duplicate}</span>
      </button>

      {/* Wrap in Container */}
      <button
        onClick={() => { onClose(); useEditorStore.getState().wrapElement(blockName, elementId) }}
        disabled={!hasParent}
        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-accent disabled:opacity-40"
      >
        <WrapText className="h-3 w-3" /> Wrap in Container
      </button>

      {/* Move Up / Move Down */}
      {parentInfo && (
        <>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => { onClose(); useEditorStore.getState().moveElement(blockName, elementId, 'up') }}
            disabled={parentInfo.index <= 0}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent disabled:opacity-40"
          >
            <span className="flex items-center gap-2"><ArrowUp className="h-3 w-3" /> Move Up</span>
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.moveUp}</span>
          </button>
          <button
            onClick={() => { onClose(); useEditorStore.getState().moveElement(blockName, elementId, 'down') }}
            disabled={!parentInfo.parent.children || parentInfo.index >= parentInfo.parent.children.length - 1}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent disabled:opacity-40"
          >
            <span className="flex items-center gap-2"><ArrowDown className="h-3 w-3" /> Move Down</span>
            <span className="text-[10px] text-muted-foreground">{SHORTCUT.moveDown}</span>
          </button>
        </>
      )}

      {/* Select Parent */}
      {hasParent && (
        <>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={() => {
              onClose()
              useEditorStore.getState().select(parentInfo.parent.id, blockName)
            }}
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs hover:bg-accent"
          >
            <span className="flex items-center gap-2"><ArrowUpToLine className="h-3 w-3" /> Select Parent</span>
            <span className="text-[10px] text-muted-foreground">Esc</span>
          </button>
        </>
      )}

      <div className="my-1 h-px bg-border" />

      {/* Delete */}
      <button
        onClick={() => {
          onClose()
          useEditorStore.getState().removeElement(blockName, elementId)
          useEditorStore.getState().clearSelection()
        }}
        disabled={!hasParent}
        className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-40"
      >
        <span className="flex items-center gap-2"><Trash2 className="h-3 w-3" /> Delete</span>
        <span className="text-[10px] text-muted-foreground">{SHORTCUT.delete}</span>
      </button>
    </div>,
    document.body,
  )
})
